import React, { useState, useEffect } from 'react';
import {
  transactionsApi,
  BirdSaleTransactionRequest,
  PaymentMethod,
  formatCurrency,
} from '../../api/transactions';
import { birdsApi } from '../../api/birds';
import AmountInput from '../ui/AmountInput';


interface Bird {
  id: string;
  bandNumber: string;
  species: {
    id: string;
    commonName: string;
    scientificName: string;
  };
  sex: 'MALE' | 'FEMALE' | 'UNKNOWN';
  dateOfBirth?: string;
  notes?: string;
}



interface BirdSaleTransactionFormProps {
  onSuccess?: (transactionId: string) => void;
  onCancel?: () => void;
}

export const BirdSaleTransactionForm: React.FC<BirdSaleTransactionFormProps> = ({
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    birdId: '',
    buyerName: '',
    buyerPhone: '',
    buyerAddress: '',
    salePrice: 0,
    paymentMethod: PaymentMethod.CASH,
    saleDate: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [availableBirds, setAvailableBirds] = useState<Bird[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const birdsResponse = await birdsApi.getBirds();
      
      // Extract data from API responses using type assertions to handle union types
      const birds = Array.isArray(birdsResponse) 
        ? birdsResponse 
        : (birdsResponse as any)?.data || [];
      
      // Map API Bird to component Bird interface
      const mappedBirds: Bird[] = birds.map((bird: any) => ({
        id: bird.id,
        bandNumber: bird.bandId || bird.bandNumber || 'Unknown',
        species: {
          id: bird.species?.id || bird.speciesId || '',
          commonName: bird.species?.commonName || 'Unknown Species',
          scientificName: bird.species?.scientificName || ''
        },
        sex: bird.gender || bird.sex || 'UNKNOWN',
        dateOfBirth: bird.hatchingDate || bird.dateOfBirth,
        notes: bird.notes
      }));
      
      setAvailableBirds(mappedBirds);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const calculateTotal = () => {
    return formData.salePrice || 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    const validationErrors: string[] = [];
    if (!formData.birdId) validationErrors.push('Please select a bird');
    if (!formData.buyerName.trim()) validationErrors.push('Buyer name is required');
    if (!formData.buyerPhone.trim()) validationErrors.push('Buyer phone is required');
    if (!formData.salePrice || formData.salePrice <= 0) validationErrors.push('Sale price must be greater than 0');
    if (!formData.saleDate) validationErrors.push('Sale date is required');

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    setErrors([]);

    try {
      const transactionData: BirdSaleTransactionRequest = {
        birdId: formData.birdId,
        salePrice: formData.salePrice,
        saleDate: new Date(formData.saleDate),
        buyerName: formData.buyerName,
        buyerPhone: formData.buyerPhone,
        buyerAddress: formData.buyerAddress,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes
      };

      const result = await transactionsApi.createBirdSaleTransaction(transactionData);
      if (onSuccess) {
        onSuccess(result.id);
      }
    } catch (error: any) {
      console.error('Error creating bird sale transaction:', error);
      setErrors([error.message || 'Failed to create bird sale transaction']);
    } finally {
      setIsLoading(false);
    }
  };

  const getBirdDisplay = (birdId: string) => {
    const bird = availableBirds.find(b => b.id === birdId);
    return bird ? `${bird.bandNumber} - ${bird.species.commonName}` : 'Unknown Bird';
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold text-slate-900 mb-6">Bird Sale Transaction</h2>

      {errors.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <h3 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h3>
          <ul className="list-disc pl-5 text-sm text-red-700">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Buyer Information */}
        <div className="bg-slate-50/80 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Buyer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="buyerName" className="block text-sm font-medium text-slate-700 mb-1">
                Buyer Name *
              </label>
              <input
                type="text"
                id="buyerName"
                name="buyerName"
                value={formData.buyerName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label htmlFor="buyerPhone" className="block text-sm font-medium text-slate-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                id="buyerPhone"
                name="buyerPhone"
                value={formData.buyerPhone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="buyerAddress" className="block text-sm font-medium text-slate-700 mb-1">
                Address
              </label>
              <textarea
                id="buyerAddress"
                name="buyerAddress"
                value={formData.buyerAddress}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Sale Details */}
        <div className="bg-slate-50/80 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Sale Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="saleDate" className="block text-sm font-medium text-slate-700 mb-1">
                Sale Date *
              </label>
              <input
                type="date"
                id="saleDate"
                name="saleDate"
                value={formData.saleDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label htmlFor="paymentMethod" className="block text-sm font-medium text-slate-700 mb-1">
                Payment Method
              </label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.values(PaymentMethod).map(method => (
                  <option key={method} value={method}>{method.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Bird Selection */}
        <div className="bg-slate-50/80 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Bird</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="birdId" className="block text-sm font-medium text-slate-700 mb-1">
                Select Bird *
              </label>
              <select
                id="birdId"
                name="birdId"
                value={formData.birdId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option key="empty-option-1753461297344" value="">Select a bird</option>
                {availableBirds.map(bird => (
                  <option key={bird.id} value={bird.id}>
                    {getBirdDisplay(bird.id)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="salePrice" className="block text-sm font-medium text-slate-700 mb-1">
                Sale Price (ZAR) *
              </label>
              <AmountInput
                id="salePrice"
                name="salePrice"
                value={formData.salePrice}
                onChange={(v) => setFormData(prev => ({ ...prev, salePrice: v ?? 0 }))}
                inputClassName="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min={0}
                required
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-slate-700 mb-1">
            Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Additional notes about this sale..."
          />
        </div>

        {/* Total */}
        <div className="bg-cyan-50 p-4 rounded-lg">
          <div className="text-right">
            <span className="text-2xl font-bold text-cyan-800">
              Total: {formatCurrency(calculateTotal())}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 rounded-md text-slate-700 hover:bg-slate-50/80 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading || !formData.birdId}
            className="px-6 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Processing...' : 'Create Sale Transaction'}
          </button>
        </div>
      </form>
    </div>
  );
}; 